import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import ProvisioningStyles from 'pages/cloud-onboarding/style';
import { orderId, orderPayload, toggleSspModels, isValidOwners, uploadedFilesState } from 'pages/cloud-onboarding/store';
import { formOrderObjFromOrderDetails, resetToggleSspModal, resetOrder, formUploadObjFromOrderDetails } from 'pages/cloud-onboarding/utils';
import logger from 'utils/logger';
import NonPrivilegedADGroupModal from 'pages/cloud-onboarding/components/Form/AccessManagementSection/NonPrivilegedADGroupModal';
import PrivilegedADGroupModal from 'pages/cloud-onboarding/components/Form/AccessManagementSection/PrivilegedADGroupModal';
import AwsPrivilegedADGroupModal from 'pages/cloud-onboarding/components/Form/AccessManagementSection/AwsPrivilegedADGroupModal';

const ProvisioningHeader = React.lazy(() => import('pages/cloud-onboarding/ProvisioningHeader'));
const SspForm = React.lazy(() => import('pages/cloud-onboarding/SspForm'));
const OrderSuccessDialog = React.lazy(() => import('pages/cloud-onboarding/OrderSuccessDialog'));
const ShowCostDetails = React.lazy(() => import('pages/cloud-onboarding/ShowCostDetails'));

const CloudOnboardingProvisioning = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [order, setOrder] = useRecoilState(orderPayload);
    const [, setUploadedFiles] = useRecoilState(uploadedFilesState);
    const resetUploadFiles = useResetRecoilState(uploadedFilesState);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetValidOwners] = useRecoilState(isValidOwners);

    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    const bAppPostApi = useMutation((payload) => axios.post(endPoints.riServices.COMMON.businessCiDetails, payload));

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);

        return data;
    }
    // Helper function to fetch landing zone details
    const fetchLandingZoneDetails = async (orderData) => {
        if (orderData?.appProfile && orderData?.awsSector && orderData?.department && orderData?.appRegion && orderData?.awsEnvironment) {
            const payload = {
                applicationServiceName: orderData.appProfile,
                sector: orderData.awsSector,
                environment: orderData.awsEnvironment,
                department: orderData.department,
                applicationRegion: orderData.appRegion,
            };
            try {
                const landingZoneResponse = await axios.post(endPoints.aws.Ec2.awsLandingZone, payload);
                return landingZoneResponse?.data;
            } catch (error) {
                logger.error('Landing zone API error:', error);
                return null;
            }
        }
        return null;
    };
    async function checkCatalog() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}`);
        const costs = await getCosts();
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetailsV2}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            const uploadObj = formUploadObjFromOrderDetails(catalogServiceId, orderDetails);
            const landingZoneDetails = await fetchLandingZoneDetails(orderObj);
            let bAppResponse = null;
            if (orderObj?.businessApp) {
                bAppResponse = await bAppPostApi?.mutateAsync({
                    businessApplication: orderObj?.businessApp,
                    applicationService: null,
                });
            }
            setUploadedFiles(uploadObj);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                businessAppDetails: bAppResponse?.data?.applications?.[0],
                landingZoneDetails,
                emptyCost: costs,
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetToggleSspModels(resetToggleSspModal);
        resetValidOwners({
            isvalidAppOwner: true,
        });
        resetUploadFiles();
    };

    React.useEffect(() => {
        if (catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>Cloud Onboarding</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} data-testid="index-form" className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <OrderSuccessDialog />
                        <NonPrivilegedADGroupModal />
                        {order?.cloudType !== 'AWS' && <PrivilegedADGroupModal />}
                        {order?.cloudType === 'AWS' && <AwsPrivilegedADGroupModal />}
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default CloudOnboardingProvisioning;
